import React, { useState, useEffect } from 'react';
import { UserSession, UserRole } from './types';
import { Navigation } from './components/Navigation';
import { Sidebar } from './components/Sidebar';
import { DashboardView } from './components/DashboardView';
import { ClientsView } from './components/ClientsView';
import { SchedulingView } from './components/SchedulingView';
import { ClinicalDocsView } from './components/ClinicalDocsView';
import { AuthorizationsView } from './components/AuthorizationsView';
import { BillingConsoleView } from './components/BillingConsoleView';
import { AdminSystemView } from './components/AdminSystemView';
import { GeminiDrawer } from './components/GeminiDrawer';
import { IncomingFaxesModal } from './components/IncomingFaxesModal';
import { UniversalToolsBar } from './components/UniversalToolsBar';
import { UniversalCallModal } from './components/UniversalCallModal';
import { ClientFaxModal } from './components/ClientFaxModal';
import { canManageClientOperations } from './utils/clientOperationsAccess';
import { PayrollView } from './components/PayrollView';
import { MyPayView } from './components/MyPayView';
import { AssignmentsServicesView } from './components/AssignmentsServicesView';
import { AccessGate } from './components/AccessGate';
import { ClinicalBrandMark } from './components/ClinicalBrandMark';
import { NewClinicalDocModal } from './components/NewClinicalDocModal';
import { MobileSoapSessionView } from './components/MobileSoapSessionView';
import { NotesView } from './components/NotesView';
import { ProviderManagementView } from './components/ProviderManagementView';
import { ProviderActivityView } from './components/ProviderActivityView';
import { AdminConnectionsSetupView } from './components/AdminConnectionsSetupView';
import { OwnerAiDataUsageView } from './components/OwnerAiDataUsageView';
import { MobileAssistantBottomNav } from './components/MobileAssistantBottomNav';
import { MobileSupervisorBottomNav } from './components/MobileSupervisorBottomNav';
import { MobileReferralCoordinatorBottomNav } from './components/MobileReferralCoordinatorBottomNav';
import { MobileOwnerBottomNav } from './components/MobileOwnerBottomNav';
import { MobileAssistantView } from './components/MobileAssistantView';
import { ArrowLeft } from 'lucide-react';
import { auth, signOut } from './firebase';
import { triggerFirstAuthSonicLogo, ensureSonicIntroHandledOnSessionRestore } from './utils/sonicLogo';
import { playPulseSeal } from './utils/pulseAudio';
import { playOwnerBloomOpenSound } from './utils/bloomOpenSound';
import { fetchDashboardStatsJson, tabRefreshLoadsDashboardStats } from './utils/dashboardStatsRefresh';

export default function App() {
  const [user, setUser] = useState<UserSession | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [usersAvailable, setUsersAvailable] = useState<any[]>([]);

  const [currentTab, setCurrentTab] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return 'clients';
    }
    return 'dashboard';
  });
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientReturnTab, setClientReturnTab] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [showIncomingFaxes, setShowIncomingFaxes] = useState(false);
  const [showUniversalCall, setShowUniversalCall] = useState(false);
  const [showUniversalFax, setShowUniversalFax] = useState(false);
  const [universalFaxClient, setUniversalFaxClient] = useState<any | null>(null);
  const [isMobileUniversalToolsOpen, setIsMobileUniversalToolsOpen] = useState(false);

  // Canonical Session Modal (Direct SOAP Documentation Flow)
  const [canonicalSessionModal, setCanonicalSessionModal] = useState<{
    isOpen: boolean;
    docType?: 'EVAL' | 'POC' | 'QPR' | 'SOAP';
    clientId?: string;
    dos?: string;
    startTime?: string;
    providerId?: string;
    serviceCode?: string;
  } | null>(null);

  // External Auth Notice Email Request
  const [externalEmailRequest, setExternalEmailRequest] = useState<{
    clientId: string;
    templateKey: string;
    authContext?: any;
    timestamp: number;
  } | null>(null);

  // Billing & Client Inner-Tab Deep Link Navigation State
  const [billingInitialTab, setBillingInitialTab] = useState<'Readiness' | 'Claims' | 'Payments' | 'Remittances'>('Readiness');
  const [billingBatchCacheStaleWarning, setBillingBatchCacheStaleWarning] = useState<string | null>(null);
  const [authInitialAction, setAuthInitialAction] = useState<'wellsky' | 'upload' | 'new' | null>(null);
  const [initialClientChartTab, setInitialClientChartTab] = useState<'Overview' | 'Auths' | 'Goals' | 'Visits' | 'Clinical Docs' | 'Billing' | 'Notes'>('Overview');

  // Gemini Drawer State
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const [aiInitialPrompt, setAiInitialPrompt] = useState('');

  // Intake Packet workflow trigger
  const [openIntakeTrigger, setOpenIntakeTrigger] = useState(0);
  // New Client modal trigger
  const [openNewClientTrigger, setOpenNewClientTrigger] = useState(0);

  // Note Approval tab deep linking - SUPERVISOR defaults to NEEDS_APPROVAL
  const [notesInitialTab, setNotesInitialTab] = useState<'CURRENT_PERIOD' | 'NEEDS_APPROVAL' | 'ALL'>('CURRENT_PERIOD');
  const [clinicalInitialDocId, setClinicalInitialDocId] = useState<string | null>(null);
  const [clientsNavTrigger, setClientsNavTrigger] = useState<{
    viewMode: 'roster' | 'expirations';
    filter?: 'all' | '30days' | '60days' | 'exhausted' | 'poc_only' | 'auth_only';
    timestamp: number;
  } | null>(null);

  // Keep notesInitialTab synchronized when operating as supervisor
  useEffect(() => {
    const isMobilePhone = typeof window !== 'undefined' && window.innerWidth <= 768;
    if (user?.role === 'SUPERVISOR') {
      setNotesInitialTab('NEEDS_APPROVAL');
      if (isMobilePhone && (currentTab === 'dashboard' || currentTab === 'mobile-soap' || currentTab === 'clients' || currentTab === 'assignments' || !isTabAllowedForRole(currentTab, 'SUPERVISOR'))) {
        setCurrentTab('notes');
      }
    } else if (user?.role === 'ASSISTANT') {
      if (isMobilePhone && (currentTab === 'dashboard' || currentTab === 'assignments' || !isTabAllowedForRole(currentTab, 'ASSISTANT'))) {
        setCurrentTab('mobile-soap');
      }
    } else if (user?.role === 'REFERRAL_COORDINATOR' || user?.role === 'OFFICE') {
      if (isMobilePhone && !isTabAllowedForRole(currentTab, user.role)) {
        setCurrentTab('dashboard');
      }
    } else if (user?.role === 'OWNER' || user?.role === 'ADMIN') {
      if (isMobilePhone && currentTab !== 'mobile-soap' && (currentTab === 'assignments' || !isTabAllowedForRole(currentTab, user.role))) {
        setCurrentTab('clients');
      }
    }
  }, [user?.role]);

  // Ensure currentTab is always allowed for active role (redirects to landing tab if attempted)
  useEffect(() => {
    if (user?.role && !isTabAllowedForRole(currentTab, user.role)) {
      setCurrentTab(getLandingTabForRole(user.role));
    }
  }, [user?.role, currentTab]);

  const handleNavigateTab = (tab: string, subTab?: string) => {
    const role = user?.role || 'ASSISTANT';
    const effectiveTab = isTabAllowedForRole(tab, role) ? tab : getLandingTabForRole(role);
    setCurrentTab(effectiveTab);
    if (effectiveTab === 'clinical') {
      if (subTab) {
        setClinicalInitialDocId(subTab);
      } else {
        setClinicalInitialDocId(null);
      }
    } else if (effectiveTab === 'notes') {
      if (subTab === 'NEEDS_APPROVAL' || subTab === 'ALL' || subTab === 'CURRENT_PERIOD') {
        setNotesInitialTab(subTab);
      } else if (user?.role === 'SUPERVISOR') {
        setNotesInitialTab('NEEDS_APPROVAL');
      } else {
        setNotesInitialTab('CURRENT_PERIOD');
      }
    } else if (effectiveTab === 'billing') {
      if (subTab === 'claims' || subTab === 'Claims') {
        setBillingInitialTab('Claims');
      } else if (subTab === 'ledger' || subTab === 'payments' || subTab === 'Payments') {
        setBillingInitialTab('Payments');
      } else if (subTab === 'reconciliation' || subTab === 'remittances' || subTab === 'Remittances') {
        setBillingInitialTab('Remittances');
      } else {
        setBillingInitialTab('Readiness');
      }
    } else if (effectiveTab === 'authorizations') {
      if (subTab === 'wellsky') {
        setAuthInitialAction('wellsky');
      } else if (subTab === 'upload' || subTab === 'new') {
        setAuthInitialAction(subTab as 'upload' | 'new');
      } else {
        setAuthInitialAction(null);
      }
    } else if (effectiveTab === 'clients') {
      if (['Overview', 'Auths', 'Goals', 'Visits', 'Clinical Docs', 'Billing', 'Notes'].includes(subTab as any)) {
        setInitialClientChartTab(subTab as any);
      } else if (subTab === 'expirations' || subTab === 'auth_only' || subTab === 'poc_only' || subTab === '60days' || subTab === '30days' || subTab === 'all') {
        setSelectedClientId(null);
        setClientsNavTrigger({
          viewMode: 'expirations',
          filter: (subTab === 'auth_only' || subTab === 'poc_only' || subTab === '60days' || subTab === '30days' || subTab === 'all') ? subTab : undefined,
          timestamp: Date.now()
        });
      } else if (!subTab) {
        setClientsNavTrigger({
          viewMode: 'roster',
          timestamp: Date.now()
        });
      }
    }
    if (effectiveTab !== 'clients') {
      setSelectedClientId(null);
      setClientReturnTab(null);
    }
    if (tabRefreshLoadsDashboardStats(effectiveTab)) {
      void fetchStats();
    }
  };

  // Storage helpers with try-catch safety
  const getStoredToken = (): string | null => {
    try {
      const t = localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token');
      return t && t !== 'undefined' && t !== 'null' && t.trim().length > 0 ? t : null;
    } catch {
      return null;
    }
  };

  const setStoredToken = (token: string) => {
    try {
      localStorage.setItem('allegiant_session_token', token);
      sessionStorage.setItem('allegiant_session_token', token);
    } catch (_) {}
  };

  const clearStoredToken = () => {
    try {
      localStorage.removeItem('allegiant_session_token');
      sessionStorage.removeItem('allegiant_session_token');
    } catch (_) {}
  };

  const getLastActiveRole = (): string | null => {
    try {
      const r = localStorage.getItem('allegiant_last_active_role');
      return r && r !== 'undefined' && r !== 'null' && r.trim().length > 0 ? r.trim() : null;
    } catch {
      return null;
    }
  };

  const setLastActiveRole = (role: string) => {
    try {
      localStorage.setItem('allegiant_last_active_role', role);
    } catch (_) {}
  };

  const isTabAllowedForRole = (tab: string, role: UserSession['role']): boolean => {
    switch (role) {
      case 'OWNER':
        return ['dashboard', 'clients', 'assignments', 'clinical', 'authorizations', 'billing', 'claims', 'payroll', 'supervision', 'provider-activity', 'connections', 'admin', 'ai-usage', 'mobile-soap', 'my-pay', 'visit-planner'].includes(tab);
      case 'ADMIN':
        return ['dashboard', 'clients', 'assignments', 'clinical', 'authorizations', 'billing', 'claims', 'payroll', 'supervision', 'provider-activity', 'connections', 'admin', 'mobile-soap', 'my-pay', 'visit-planner'].includes(tab);
      case 'REFERRAL_COORDINATOR':
      case 'OFFICE':
        return ['dashboard', 'clients', 'assignments', 'visit-planner', 'scheduling', 'clinical', 'authorizations', 'supervision', 'provider-activity'].includes(tab);
      case 'SUPERVISOR':
        return ['notes', 'clients', 'assignments', 'visit-planner', 'clinical', 'mobile-soap', 'my-pay', 'supervision', 'assistant'].includes(tab);
      case 'ASSISTANT':
        return ['clients', 'assignments', 'visit-planner', 'clinical', 'mobile-soap', 'my-pay', 'assistant'].includes(tab);
      default:
        return ['clients', 'clinical'].includes(tab);
    }
  };

  const getLandingTabForRole = (role: UserSession['role']): string => {
    const isPhone = typeof window !== 'undefined' && window.innerWidth <= 768;
    switch (role) {
      case 'OWNER':
      case 'ADMIN':
        return isPhone ? 'clients' : 'dashboard';
      case 'REFERRAL_COORDINATOR':
        return 'dashboard';
      case 'OFFICE':
        return isPhone ? 'dashboard' : 'clients';
      case 'SUPERVISOR':
        return 'notes';
      case 'ASSISTANT':
      default:
        return isPhone ? 'mobile-soap' : 'clients';
    }
  };

  const handleSwitchRole = async (targetRole: UserRole) => {
    if (!user || user.role === targetRole) return;

    // Trigger canonical sound on successful role switch
    const triggerRoleSwitchSound = () => {
      // Plays the single canonical Pulse sound (/pulse-seal.wav) on verified role switch
      playPulseSeal({ cooldownMs: 350, volume: 1.0 });
    };

    const isPhone = typeof window !== 'undefined' && window.innerWidth <= 768;
    const determineNextTab = (role: UserRole): string => {
      if (isPhone) {
        return getLandingTabForRole(role);
      }
      return role === 'REFERRAL_COORDINATOR'
        ? 'dashboard'
        : (!isTabAllowedForRole(currentTab, role) ? getLandingTabForRole(role) : currentTab);
    };

    try {
      const token = getStoredToken();
      const res = await fetch('/api/auth/switch-role', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ targetRole }),
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok && data.user) {
        if (data.token) {
          setStoredToken(data.token);
        }
        setLastActiveRole(targetRole);
        setUser(data.user);
        triggerRoleSwitchSound();
        const nextTab = determineNextTab(targetRole);
        setCurrentTab(nextTab);
        if (nextTab === 'dashboard') {
          fetchStats();
        }
      } else {
        const updatedUser: UserSession = {
          ...user,
          role: targetRole,
          activeRole: targetRole
        };
        setUser(updatedUser);
        triggerRoleSwitchSound();
        const nextTab = determineNextTab(targetRole);
        setCurrentTab(nextTab);
        if (nextTab === 'dashboard') {
          fetchStats();
        }
      }
    } catch (e) {
      console.error('Failed to switch active role:', e);
      const updatedUser: UserSession = {
        ...user,
        role: targetRole,
        activeRole: targetRole
      };
      setUser(updatedUser);
      triggerRoleSwitchSound();
      const nextTab = determineNextTab(targetRole);
      setCurrentTab(nextTab);
      if (nextTab === 'dashboard') {
        fetchStats();
      }
    }
  };

  const handleUserAuthenticated = (loggedInUser: UserSession) => {
    setUser(loggedInUser);
    setAuthLoading(false); // Unblock immediately!
    playOwnerBloomOpenSound(loggedInUser);

    // Role safety: close disallowed role-specific modals and clear stale workspace state
    setCanonicalSessionModal(null);
    setExternalEmailRequest(null);
    setIsAIDrawerOpen(false);
    setSelectedClientId(null);
    setSelectedClient(null);
    setClinicalInitialDocId(null);

    const targetTab = getLandingTabForRole(loggedInUser.role);
    setCurrentTab(targetTab);
    if (targetTab === 'dashboard') {
      fetchStats();
    }
    fetchSupplementaryData(loggedInUser);
  };

  const handleUserUnauthenticated = () => {
    setUser(null);
    setAuthLoading(false); // Unblock immediately so AccessGate renders!
  };

  const checkBackendSession = async (explicitToken?: string): Promise<UserSession | null> => {
    try {
      const token = explicitToken || getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Session-Token'] = token;
      }
      const res = await fetch('/api/auth/me', { headers, credentials: 'include' });
      if (res.ok) {
        let data: any = null;
        try {
          const text = await res.text();
          data = text ? JSON.parse(text) : null;
        } catch (_) {
          data = null;
        }
        if (data && data.authenticated && data.user) {
          if (data.usersAvailable) setUsersAvailable(data.usersAvailable);
          return data.user;
        }
      }
    } catch (err) {
      console.warn('[Backend Session] check error:', err);
    }
    return null;
  };

  const fetchSupplementaryData = async (loggedInUser: UserSession) => {
    try {
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Session-Token'] = token;
      }
      const res = await fetch('/api/auth/me', { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.usersAvailable) setUsersAvailable(data.usersAvailable);
        if (data.user) {
          // Merge missing canonical fields while strictly preserving current session's active role context
          setUser((currentUser) => {
            if (!currentUser) return currentUser;
            const mergedAssignedRoles = (data.user.assignedRoles && data.user.assignedRoles.length > 0)
              ? data.user.assignedRoles
              : currentUser.assignedRoles;
            const roleStillAllowed = !mergedAssignedRoles?.length || mergedAssignedRoles.includes(currentUser.role);
            return {
              ...currentUser,
              assignedRoles: mergedAssignedRoles,
              providerId: data.user.providerId,
              providerName: data.user.providerName,
              providerCredential: data.user.providerCredential,
              role: roleStillAllowed ? currentUser.role : (data.user.role || currentUser.role),
              activeRole: roleStillAllowed
                ? (currentUser.activeRole || currentUser.role)
                : (data.user.activeRole || data.user.role)
            };
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch supplementary user data:', err);
    }
  };

  const fetchStats = async () => {
    const data = await fetchDashboardStatsJson();
    if (data !== undefined) {
      setStats(data);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Hard safety timeout: under NO circumstance remain stuck on "VERIFYING ALLEGIANT AUTHORIZATION..." longer than 2.5s
    const safetyTimer = setTimeout(() => {
      if (isMounted) {
        setAuthLoading(false);
      }
    }, 2500);

    // Immediate session verification flow
    const initAuth = async () => {
      try {
        // 1. Check existing backend session (cookie or stored HMAC token)
        const sessionUser = await checkBackendSession();
        if (sessionUser && isMounted) {
          clearTimeout(safetyTimer);
          ensureSonicIntroHandledOnSessionRestore();
          handleUserAuthenticated(sessionUser);
          return;
        }

        // 2. Check if Firebase currentUser already exists and exchange token
        if (auth.currentUser && isMounted) {
          try {
            const idToken = await auth.currentUser.getIdToken();
            const preferredRole = getLastActiveRole();
            const res = await fetch('/api/auth/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              credentials: 'include',
              body: JSON.stringify({ idToken, preferredRole })
            });
            if (res.ok) {
              let data: any = null;
              try {
                const text = await res.text();
                data = text ? JSON.parse(text) : null;
              } catch (_) {
                data = null;
              }
              if (data?.success && data?.user && isMounted) {
                if (data.token) setStoredToken(data.token);
                clearTimeout(safetyTimer);
                ensureSonicIntroHandledOnSessionRestore();
                handleUserAuthenticated(data.user);
                return;
              }
            }
          } catch (err) {
            console.warn('[Auth Bootstrap] Direct Firebase token exchange error:', err);
          }
        }
      } catch (err) {
        console.warn('[Auth Bootstrap] Initial verification error:', err);
      }

      // If no valid session or Google user on initial check, dismiss loading immediately to render AccessGate
      if (isMounted) {
        clearTimeout(safetyTimer);
        handleUserUnauthenticated();
      }
    };

    initAuth();

    // 3. Listen for asynchronous Firebase auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!isMounted) return;

      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          const preferredRole = getLastActiveRole();
          const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            credentials: 'include',
            body: JSON.stringify({ idToken, preferredRole })
          });
          if (res.ok) {
            let data: any = null;
            try {
              const text = await res.text();
              data = text ? JSON.parse(text) : null;
            } catch (_) {
              data = null;
            }
            if (data?.success && data?.user && isMounted) {
              if (data.token) setStoredToken(data.token);
              clearTimeout(safetyTimer);
              handleUserAuthenticated(data.user);
              return;
            }
          }
        } catch (err) {
          console.warn('[Auth Bootstrap] onAuthStateChanged login error:', err);
        }
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    try {
      clearStoredToken();
      await signOut(auth).catch(() => {});
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.error('Failed to log out:', e);
    }
    setUser(null);
    setStats(null);
    setSelectedClientId(null);
    setSelectedClient(null);
    setCurrentTab('dashboard');
  };

  useEffect(() => {
    if (selectedClientId && user) {
      fetch(`/api/clients/${selectedClientId}`)
        .then(res => res.json())
        .then(data => setSelectedClient(data?.client || null))
        .catch(() => setSelectedClient(null));
    } else {
      setSelectedClient(null);
    }
  }, [selectedClientId, user]);

  const handleSwitchUser = async (userId: string) => {
    try {
      const token = getStoredToken();
      const res = await fetch('/api/auth/switch-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.token) setStoredToken(data.token);
        setUser(data.user);
        if (data.user?.role) setLastActiveRole(data.user.role);
        setSelectedClientId(null);
        setSelectedClient(null);
        const isClinical = data.user.role === 'ASSISTANT' || data.user.role === 'SUPERVISOR';
        if (isClinical) {
          const isPhone = typeof window !== 'undefined' && window.innerWidth <= 768;
          setCurrentTab(data.user.role === 'SUPERVISOR' ? 'notes' : isPhone ? 'mobile-soap' : 'clients');
        } else {
          setCurrentTab('dashboard');
          await fetchStats();
        }
      }
    } catch (err) {
      console.error('Failed to switch user:', err);
    }
  };

  const handleOpenAIWithPrompt = (prompt: string, clientId?: string) => {
    setAiInitialPrompt(prompt);
    if (clientId) setSelectedClientId(clientId);
    setIsAIDrawerOpen(true);
  };

  const handleOpenAuthEmail = (clientId: string, templateKey: 'auth_7day' | 'auth_3day', authContext?: any) => {
    setSelectedClientId(clientId);
    setExternalEmailRequest({
      clientId,
      templateKey,
      authContext,
      timestamp: Date.now()
    });
  };

  const [plannedSoapVisit, setPlannedSoapVisit] = useState<{
    serviceId?: string;
    clientId?: string;
    dos?: string;
    startTime?: string;
    serviceCode?: string;
  } | null>(null);

  const handleStartSession = (context: {
    clientId?: string;
  } = {}) => {
    // Safety check: REFERRAL_COORDINATOR and non-clinical staff do not author clinical notes
    if (user?.role === 'REFERRAL_COORDINATOR' || user?.role === 'OFFICE') {
      console.warn('Direct clinical note authoring requires a clinical role (SUPERVISOR or ASSISTANT)');
      return;
    }
    if (context.clientId) {
      setSelectedClientId(context.clientId);
    }
    setPlannedSoapVisit(null);
    setCurrentTab('mobile-soap');
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f1f5f9] text-slate-800 flex flex-col items-center justify-center space-y-4">
        <ClinicalBrandMark size={56} className="shrink-0" />
        <div className="text-xs font-mono text-slate-500 tracking-wider uppercase">
          Verifying Bloom Authorization...
        </div>
      </div>
    );
  }

  // Access Gate: Deny all unauthenticated or unauthorized users before seeing any application data
  if (!user) {
    return (
      <AccessGate
        onLoginSuccess={(loggedInUser) => {
          if (loggedInUser.role !== 'OWNER' && loggedInUser.activeRole !== 'OWNER') {
            triggerFirstAuthSonicLogo(loggedInUser);
          }
          handleUserAuthenticated(loggedInUser);
        }}
      />
    );
  }

  const isDesktopLockedFooterRole =
    user.role === 'OWNER' ||
    user.role === 'ADMIN' ||
    user.role === 'REFERRAL_COORDINATOR' ||
    user.role === 'OFFICE';

  const isClinical = user.role === 'SUPERVISOR' || user.role === 'ASSISTANT';

  return (
    <div
      className={`min-h-screen min-h-[100dvh] ${
        isDesktopLockedFooterRole ? 'md:h-screen md:overflow-hidden' : ''
      } bg-[#f1f5f9] text-slate-900 flex flex-col font-sans selection:bg-sky-500 selection:text-white`}
    >
      {/* Top Navigation */}
      <Navigation
        user={user}
        onLogout={handleLogout}
        onSwitchRole={handleSwitchRole}
        companyUsers={usersAvailable}
        onSwitchUser={handleSwitchUser}
        showUserSwitch={
          user.role === 'OWNER' ||
          user.activeRole === 'OWNER' ||
          (user.assignedRoles || []).includes('OWNER') ||
          Boolean(user.operatingAsOwnerId)
        }
        onOpenAI={() => handleOpenAIWithPrompt('')}
        onOpenIncomingFaxes={() => setShowIncomingFaxes(true)}
        onNavigateTab={handleNavigateTab}
        onOpenIntakePacket={() => {
          setCurrentTab('clients');
          setSelectedClientId(null);
          setClientReturnTab(null);
          setOpenIntakeTrigger((prev) => prev + 1);
        }}
        onOpenNewClient={() => {
          setCurrentTab('clients');
          setSelectedClientId(null);
          setClientReturnTab(null);
          setOpenNewClientTrigger((prev) => prev + 1);
        }}
      />

      {/* Main Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          currentTab={currentTab}
          onSelectTab={(tab) => {
            const targetTab = isTabAllowedForRole(tab, user.role) ? tab : getLandingTabForRole(user.role);
            setCurrentTab(targetTab);
            if (targetTab !== 'clients') {
              setSelectedClientId(null);
              setClientReturnTab(null);
            }
          }}
          userRole={user.role}
          onOpenNewSession={() => handleStartSession()}
          onOpenIntakePacket={() => {
            setCurrentTab('clients');
            setSelectedClientId(null);
            setClientReturnTab(null);
            setOpenIntakeTrigger((prev) => prev + 1);
          }}
          onOpenNewClient={() => {
            setCurrentTab('clients');
            setSelectedClientId(null);
            setClientReturnTab(null);
            setOpenNewClientTrigger((prev) => prev + 1);
          }}
          onOpenAI={() => {
            if (isAIDrawerOpen) {
              setIsAIDrawerOpen(false);
            } else {
              handleOpenAIWithPrompt('');
            }
          }}
        />

        {/* Content View Container */}
        <div
          className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-[#f1f5f9]"
          data-role={user.role}
        >
          <main
            className={`flex-1 overflow-y-auto ${
              user.role === 'REFERRAL_COORDINATOR' || user.role === 'OFFICE'
                ? 'p-3 sm:p-5 md:p-6 lg:p-8 pb-[calc(env(safe-area-inset-bottom,0px)+4.5rem)] md:pb-6 lg:pb-8'
                : user.role === 'OWNER' || user.role === 'ADMIN'
                ? 'p-3 sm:p-5 md:p-6 lg:p-8 pb-[calc(env(safe-area-inset-bottom,0px)+4.5rem)] md:pb-6 lg:pb-8'
                : user.role === 'ASSISTANT' || user.role === 'SUPERVISOR'
                ? 'p-3 sm:p-5 md:p-6 lg:p-8 pb-[calc(env(safe-area-inset-bottom,0px)+4.5rem)] md:pb-24'
                : 'p-6 lg:p-8 pb-24'
            }`}
          >
          <div className="max-w-7xl mx-auto space-y-6">
            {currentTab === 'dashboard' && (
              <DashboardView
                stats={stats}
                userRole={user.role}
                onNavigateTab={handleNavigateTab}
                onOpenAIWithPrompt={handleOpenAIWithPrompt}
                onSelectClient={(id) => {
                  setClientReturnTab(currentTab);
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
                onSelectService={(srvId) => handleNavigateTab('assignments')}
                onOpenAuthEmail={handleOpenAuthEmail}
                onRefreshStats={fetchStats}
              />
            )}

            {currentTab === 'clients' && (
              <ClientsView
                userRole={user.role}
                providerCredential={user.providerCredential}
                selectedClientId={selectedClientId}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  if (id === null && clientReturnTab) {
                    setCurrentTab(clientReturnTab);
                    setClientReturnTab(null);
                  }
                }}
                onOpenAIWithContext={(prompt, cid) => handleOpenAIWithPrompt(prompt, cid)}
                onOpenAIWithPrompt={handleOpenAIWithPrompt}
                onOpenUniversalTools={() => setIsMobileUniversalToolsOpen(true)}
                openIntakeTrigger={openIntakeTrigger}
                openNewClientTrigger={openNewClientTrigger}
                navTrigger={clientsNavTrigger}
                initialChartTab={initialClientChartTab}
              />
            )}

            {(currentTab === 'assignments' || currentTab === 'scheduling' || currentTab === 'visit-planner') && isTabAllowedForRole(currentTab, user.role) && (
              <div className="space-y-3">
                {user.role === 'ASSISTANT' && (
                  <div className="md:hidden">
                    <button
                      type="button"
                      onClick={() => handleNavigateTab('mobile-soap')}
                      className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-white hover:bg-slate-50/90 border border-slate-200/90 text-slate-700 hover:text-slate-900 font-medium text-xs shadow-xs transition-all active:scale-[0.99] cursor-pointer"
                    >
                      <div className="flex items-center space-x-2">
                        <ArrowLeft className="w-4 h-4 text-slate-600" />
                        <span>Back to SOAP Home</span>
                      </div>
                      <span className="text-[10px] font-mono text-slate-500">Assistant Hub</span>
                    </button>
                  </div>
                )}
                <AssignmentsServicesView
                  user={user}
                  userRole={user.role}
                  onNavigateTab={handleNavigateTab}
                  onSelectClient={(id) => {
                    setClientReturnTab(currentTab);
                    setSelectedClientId(id);
                    setCurrentTab('clients');
                  }}
                  onRefreshStats={fetchStats}
                />
              </div>
            )}

            {currentTab === 'assistant' && (user.role === 'ASSISTANT' || user.role === 'SUPERVISOR') && (
              <MobileAssistantView
                user={user}
                onNavigateTab={handleNavigateTab}
              />
            )}

            {currentTab === 'mobile-soap' && (
              <MobileSoapSessionView
                user={user}
                preselectedClientId={selectedClientId}
                plannedVisit={plannedSoapVisit}
                onClearPreselectedClient={() => setSelectedClientId(null)}
                onClearPlannedVisit={() => setPlannedSoapVisit(null)}
                onNavigateTab={handleNavigateTab}
                onOpenAIWithPrompt={handleOpenAIWithPrompt}
              />
            )}

            {currentTab === 'notes' && (
              <NotesView
                user={user}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
                onNavigateTab={handleNavigateTab}
                initialTab={notesInitialTab}
              />
            )}

            {currentTab === 'clinical' && (
              <ClinicalDocsView
                user={user}
                initialSelectedDocId={clinicalInitialDocId}
                onConsumeInitialDocId={() => setClinicalInitialDocId(null)}
                onRefreshStats={fetchStats}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
                onNavigateTab={(tab, sub) => handleNavigateTab(tab, sub)}
                onOpenAIWithPrompt={handleOpenAIWithPrompt}
                initialClientId={selectedClientId}
              />
            )}

            {currentTab === 'authorizations' && (
              <AuthorizationsView
                userRole={user.role}
                initialAction={authInitialAction}
                onClearInitialAction={() => setAuthInitialAction(null)}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
                onRefreshStats={fetchStats}
                onNavigateToConnections={() => setCurrentTab('connections')}
              />
            )}

            {currentTab === 'billing' && user.role !== 'ASSISTANT' && user.role !== 'SUPERVISOR' && (
              <BillingConsoleView
                userRole={user.role}
                initialTab={billingInitialTab}
                onRefreshStats={fetchStats}
                onNavigateTab={handleNavigateTab}
                batchCacheStaleWarning={billingBatchCacheStaleWarning}
                onBatchCacheStaleWarning={setBillingBatchCacheStaleWarning}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
              />
            )}

            {currentTab === 'claims' && user.role !== 'ASSISTANT' && user.role !== 'SUPERVISOR' && (
              <BillingConsoleView
                userRole={user.role}
                initialTab="Claims"
                onRefreshStats={fetchStats}
                onNavigateTab={handleNavigateTab}
                batchCacheStaleWarning={billingBatchCacheStaleWarning}
                onBatchCacheStaleWarning={setBillingBatchCacheStaleWarning}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
              />
            )}

            {currentTab === 'payroll' && user.role !== 'ASSISTANT' && user.role !== 'SUPERVISOR' && (
              <PayrollView
                userRole={user.role}
                onSelectClient={(id) => {
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
              />
            )}

            {currentTab === 'my-pay' && (
              <div className="space-y-3">
                {user.role === 'ASSISTANT' && (
                  <div className="md:hidden">
                    <button
                      type="button"
                      onClick={() => handleNavigateTab('mobile-soap')}
                      className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-white hover:bg-slate-50/90 border border-slate-200/90 text-slate-700 hover:text-slate-900 font-medium text-xs shadow-xs transition-all active:scale-[0.99] cursor-pointer"
                    >
                      <div className="flex items-center space-x-2">
                        <ArrowLeft className="w-4 h-4 text-slate-600" />
                        <span>Back to SOAP Home</span>
                      </div>
                      <span className="text-[10px] font-mono text-slate-500">Assistant Hub</span>
                    </button>
                  </div>
                )}
                <MyPayView
                  user={user}
                  onSelectClient={(id) => {
                    setSelectedClientId(id);
                    setCurrentTab('clients');
                  }}
                />
              </div>
            )}

            {currentTab === 'supervision' && (
              <ProviderManagementView
                userRole={user.role}
                onNavigateToTab={handleNavigateTab}
                onSelectClient={(id) => {
                  setClientReturnTab(currentTab);
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
              />
            )}

            {currentTab === 'provider-activity' && (user.role === 'OWNER' || user.role === 'ADMIN' || user.role === 'REFERRAL_COORDINATOR' || user.role === 'OFFICE') && (
              <ProviderActivityView
                userRole={user.role}
                onNavigateTab={handleNavigateTab}
                onSelectClient={(id) => {
                  setClientReturnTab(currentTab);
                  setSelectedClientId(id);
                  setCurrentTab('clients');
                }}
              />
            )}

            {currentTab === 'connections' && (user.role === 'ADMIN' || user.role === 'OWNER') && (
              <AdminConnectionsSetupView
                onNavigateToAuthorizations={() => setCurrentTab('authorizations')}
              />
            )}

            {currentTab === 'admin' && (user.role === 'ADMIN' || user.role === 'OWNER') && (
              <AdminSystemView
                userRole={user.role}
                subTab="admin"
                onNavigateToTab={(t) => setCurrentTab(t)}
              />
            )}

            {currentTab === 'ai-usage' && user.role === 'OWNER' && (
              <AdminSystemView
                userRole={user.role}
                initialTab="ai-usage"
                subTab="ai-usage"
                onNavigateToTab={(t) => setCurrentTab(t)}
              />
            )}
          </div>
        </main>
      </div>
    </div>

      {/* Persistent Universal Tools Bar: locked to bottom of desktop viewport for OWNER & REFERRAL_COORDINATOR */}
      <div id="pulse-universal-tools-container" className="shrink-0">
        <UniversalToolsBar
            selectedClient={selectedClient}
            userRole={user.role}
            onSelectClient={(id) => setSelectedClientId(id)}
            onCreateClinicalDoc={(clientId) => {
              setSelectedClientId(clientId);
              setCurrentTab('clinical-docs');
            }}
            onOpenAI={(prompt) => handleOpenAIWithPrompt(prompt, selectedClientId || undefined)}
            onNavigateToTab={(tab) => setCurrentTab(tab)}
            onRefreshData={fetchStats}
            onOpenCall={() => {
              if (canManageClientOperations(user.role)) setShowUniversalCall(true);
            }}
            onOpenFax={(client) => {
              if (canManageClientOperations(user.role)) {
                setUniversalFaxClient(client || null);
                setShowUniversalFax(true);
              }
            }}
            externalEmailRequest={externalEmailRequest}
            isMobileSheetOpen={isMobileUniversalToolsOpen}
            onCloseMobileSheet={() => setIsMobileUniversalToolsOpen(false)}
          />
        </div>

      {/* Mobile Assistant Fixed Bottom Navigation — strictly phone widths (<= 768px) and ASSISTANT role */}
      {user.role === 'ASSISTANT' && (
        <MobileAssistantBottomNav
          currentTab={currentTab}
          onSelectTab={(tab) => handleNavigateTab(tab)}
          user={user}
          onSwitchRole={handleSwitchRole}
          onLogout={handleLogout}
        />
      )}

      {/* Mobile Supervisor Fixed Bottom Navigation — strictly phone widths (<= 768px) and SUPERVISOR role */}
      {user.role === 'SUPERVISOR' && (
        <MobileSupervisorBottomNav
          currentTab={currentTab}
          onSelectTab={(tab) => handleNavigateTab(tab)}
          user={user}
          onSwitchRole={handleSwitchRole}
          onLogout={handleLogout}
        />
      )}

      {/* Mobile Referral Coordinator Fixed Bottom Navigation — strictly phone widths (<= 768px) and REFERRAL_COORDINATOR/OFFICE role */}
      {(user.role === 'REFERRAL_COORDINATOR' || user.role === 'OFFICE') && (
        <MobileReferralCoordinatorBottomNav
          currentTab={currentTab}
          onSelectTab={(tab) => handleNavigateTab(tab)}
          onOpenUniversalTools={() => setIsMobileUniversalToolsOpen(true)}
          onOpenIntakePacket={() => {
            setCurrentTab('clients');
            setSelectedClientId(null);
            setClientReturnTab(null);
            setOpenIntakeTrigger((prev) => prev + 1);
          }}
          onOpenNewClient={() => {
            setCurrentTab('clients');
            setSelectedClientId(null);
            setClientReturnTab(null);
            setOpenNewClientTrigger((prev) => prev + 1);
          }}
          user={user}
          onSwitchRole={handleSwitchRole}
          onLogout={handleLogout}
        />
      )}

      {/* Mobile Owner Fixed Bottom Navigation — strictly phone widths (<= 768px) and OWNER/ADMIN role */}
      {(user.role === 'OWNER' || user.role === 'ADMIN') && (
        <MobileOwnerBottomNav
          currentTab={currentTab}
          onSelectTab={(tab) => handleNavigateTab(tab)}
          onOpenUniversalTools={() => setIsMobileUniversalToolsOpen(true)}
          user={user}
          onSwitchRole={handleSwitchRole}
          onLogout={handleLogout}
        />
      )}

      {/* Canonical Session Modal (Direct SOAP Documentation Flow) */}
      {canonicalSessionModal?.isOpen && user && (
        <NewClinicalDocModal
          user={user}
          initialDocType={canonicalSessionModal.docType || 'SOAP'}
          initialClientId={canonicalSessionModal.clientId}
          initialDos={canonicalSessionModal.dos}
          initialStartTime={canonicalSessionModal.startTime}
          initialProviderId={canonicalSessionModal.providerId}
          initialServiceCode={canonicalSessionModal.serviceCode}
          onClose={() => setCanonicalSessionModal(null)}
          onSuccess={async () => {
            setCanonicalSessionModal(null);
            await fetchStats();
          }}
        />
      )}

      {/* Sliding Allegiant Gemini Assistant Drawer */}
      <GeminiDrawer
        isOpen={isAIDrawerOpen}
        onClose={() => setIsAIDrawerOpen(false)}
        user={user}
        contextScreen={currentTab}
        selectedClientId={selectedClientId}
        initialPrompt={aiInitialPrompt}
        onSelectClient={(id, clientTab) => {
          setSelectedClientId(id);
          if (clientTab) {
            setInitialClientChartTab(clientTab);
          }
          handleNavigateTab('clients', clientTab);
        }}
        onNavigateTab={(tab, subTab) => handleNavigateTab(tab, subTab)}
        onOpenEmailDraft={(clientId) => {
          if (clientId) {
            setSelectedClientId(clientId);
          }
          setExternalEmailRequest({
            clientId: clientId || selectedClientId || '',
            templateKey: 'auth_status_update',
            timestamp: Date.now()
          });
        }}
      />

      {/* Universal Call Modal (Human-Controlled Shell) */}
      {showUniversalCall && canManageClientOperations(user.role) && (
        <UniversalCallModal
          initialClient={null}
          currentUser={user}
          onClose={() => setShowUniversalCall(false)}
        />
      )}

      {/* Universal Quick Fax Modal */}
      {showUniversalFax && canManageClientOperations(user.role) && (
        <ClientFaxModal
          clientId={universalFaxClient?.id}
          clientName={universalFaxClient ? `${universalFaxClient.first_name || ''} ${universalFaxClient.last_name || ''}`.trim() : ''}
          recipientFax={universalFaxClient?.physician_fax || ''}
          recipientName={universalFaxClient?.physician_name || ''}
          onClose={() => {
            setShowUniversalFax(false);
            setUniversalFaxClient(null);
          }}
        />
      )}

      {/* Incoming Faxes Central Inbox */}
      {showIncomingFaxes && (
        <IncomingFaxesModal
          userRole={user.role}
          onClose={() => setShowIncomingFaxes(false)}
          onViewClientChart={(clientId) => {
            setSelectedClientId(clientId);
            setCurrentTab('clients');
            setShowIncomingFaxes(false);
          }}
        />
      )}
    </div>
  );
}
